import { lazy, Suspense, useEffect, useRef } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { useUser } from "@clerk/clerk-react";
import { MainLayout } from "@/components/layout";
import { PageLoader, SiteAgeGate } from "@/components/ui";
import { AnalyticsService } from "@/services/analytics";

// Eager load all main pages - they're lightweight forms/layouts
// Data fetching happens inside components (with their own loading states)
import { HomePage } from "@/pages/home-page";
import { AssetsPage } from "@/pages/assets-page";
import { GenerationPage } from "@/pages/generation-page";
import {
  VideoPage,
  TextToVideoPage,
  ImageToVideoPage,
} from "@/pages/video";
import { ImagesPage, TextToImagePage, ImageEditPage } from "@/pages/images";
import {
  CharactersPage,
  CharacterDetailPage,
  CreateCharacterPage,
  EditCharacterPage,
} from "@/pages/characters";
import { ExtendVideoPage } from "@/pages/extend-video-page";
import { PiecanRedirectPage } from "@/pages/piecan-redirect-page";

// Lazy load only placeholder/rarely visited pages
const BillingPage = lazy(() =>
  import("@/pages/billing-page").then((m) => ({ default: m.BillingPage }))
);
const CheckoutPage = lazy(() =>
  import("@/pages/checkout-page").then((m) => ({ default: m.CheckoutPage }))
);
const SupportPage = lazy(() =>
  import("@/pages/support-page").then((m) => ({ default: m.SupportPage }))
);
const LegalPage = lazy(() =>
  import("@/pages/legal-page").then((m) => ({ default: m.LegalPage }))
);

/**
 * `/generation/:id` is special: each navigation starts a brand-new
 * generation context (different model, prompt, source asset, draft
 * flag). Without remounting, the in-component state (idempotency
 * guards, polled `generationId`, `isBooting`, etc.) carries over from
 * the previous generation and short-circuits the new one — so e.g.
 * an inline edit from the character-draft success page would land on
 * `/generation/new` and never actually fire the new generate call.
 *
 * Keying on `location.key` (which changes on every push/replace, even
 * when the path is identical) forces the component to remount and
 * start fresh. Cheap because the component is short-lived anyway.
 */
function GenerationRoute() {
  const location = useLocation();
  return <GenerationPage key={location.key} />;
}

function AppRoutes() {
  const location = useLocation();
  const { isSignedIn, user: clerkUser } = useUser();
  const hasIdentifiedRef = useRef(false);
  const hasTrackedSignupRef = useRef(false);
  const lastBillingPathRef = useRef<string | null>(null);

  // Track user identification and signup
  useEffect(() => {
    if (isSignedIn && clerkUser?.id) {
      // Identify user in PostHog (only once per session)
      if (!hasIdentifiedRef.current) {
        AnalyticsService.identify(clerkUser.id, {
          email: clerkUser.primaryEmailAddress?.emailAddress,
          clerk_id: clerkUser.id,
          created_at: clerkUser.createdAt?.toISOString(),
          last_sign_in: clerkUser.lastSignInAt?.toISOString(),
          username: clerkUser.username,
          first_name: clerkUser.firstName,
          last_name: clerkUser.lastName,
        });
        hasIdentifiedRef.current = true;
      }

      // Track signup for new users (with duplicate prevention)
      if (!hasTrackedSignupRef.current && clerkUser.createdAt) {
        const userProperties = {
          email: clerkUser.primaryEmailAddress?.emailAddress,
          created_at: clerkUser.createdAt?.toISOString(),
          username: clerkUser.username,
          first_name: clerkUser.firstName,
          last_name: clerkUser.lastName,
        };
        AnalyticsService.trackSignUp(clerkUser.id, userProperties);
        AnalyticsService.trackSignUpGTM(clerkUser.id, userProperties);
        hasTrackedSignupRef.current = true;
      }
    }
  }, [isSignedIn, clerkUser]);

  // Track add_to_cart when visiting billing page
  useEffect(() => {
    if (
      location.pathname === "/billing" &&
      lastBillingPathRef.current !== "/billing"
    ) {
      AnalyticsService.trackAddToCart(clerkUser?.id);
    }
    lastBillingPathRef.current = location.pathname;
  }, [location.pathname, clerkUser?.id]);

  return (
    <MainLayout>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/assets" element={<AssetsPage />} />
          {/* Sidebar "Characters" → straight creation flow.
              Existing characters are viewed under /assets > Characters tab.
              `CharactersPage` provides the same tabs + AnimatedOutlet shell
              as VideoPage / ImagesPage so the section animates consistently
              and future entries (e.g. Image to Character) drop in cleanly. */}
          <Route path="/characters" element={<CharactersPage />}>
            <Route index element={<CreateCharacterPage />} />
            <Route path="edit" element={<EditCharacterPage />} />
          </Route>
          <Route path="/characters/:name" element={<CharacterDetailPage />} />
          <Route path="/generation/:id" element={<GenerationRoute />} />
          <Route path="/billing" element={<BillingPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/video" element={<VideoPage />}>
            <Route index element={<Navigate to="i2v" replace />} />
            <Route path="t2v" element={<TextToVideoPage />} />
            <Route path="i2v" element={<ImageToVideoPage />} />
          </Route>
          <Route path="/images" element={<ImagesPage />}>
            <Route index element={<Navigate to="t2i" replace />} />
            <Route path="t2i" element={<TextToImagePage />} />
            <Route path="image-edit" element={<ImageEditPage />} />
          </Route>
          <Route path="/extend-video" element={<ExtendVideoPage />} />
          {/* Open PieCan backdoor — the single email link
              (https://pixelmaker.ai/go/piecan). Mints a fresh referral token
              and redirects; no sign-in required. */}
          <Route path="/go/piecan" element={<PiecanRedirectPage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/terms" element={<LegalPage />} />
          <Route path="/privacy" element={<LegalPage />} />
          <Route path="/acceptable-use" element={<LegalPage />} />
          <Route path="/content-monitoring" element={<LegalPage />} />
          <Route path="/model-verification" element={<LegalPage />} />
          {/* Redirect unknown routes to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </MainLayout>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <SiteAgeGate />
      <AppRoutes />
    </BrowserRouter>
  );
}
